import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { parseSellerReviewFeedback } from "@/lib/seller-review-feedback";
import type { Item, ItemCategory, ItemStatus, Coordinates } from "@/lib/types";
import { DEFAULT_BUYER_AVAILABILITY_CONFIRMED_WINDOW_DAYS } from "@/lib/items/sellerAvailabilityConfirmed";

type ServiceSupabase = SupabaseClient<Database>;

type RecentlyConfirmedItemRow = Pick<
  Database["public"]["Tables"]["items"]["Row"],
  | "id"
  | "title"
  | "description"
  | "defects"
  | "price"
  | "quantity"
  | "category"
  | "subcategory"
  | "location"
  | "coordinates"
  | "seller_id"
  | "status"
  | "seller_review_feedback"
  | "created_at"
  | "updated_at"
  | "seller_availability_confirmed_at"
>;

const RECENTLY_CONFIRMED_ITEM_COLUMNS =
  "id,title,description,defects,price,quantity,category,subcategory,location,coordinates,seller_id,status,seller_review_feedback,created_at,updated_at,seller_availability_confirmed_at";

const VALID_CATEGORIES: readonly ItemCategory[] = [
  "baby",
  "bedding",
  "decor",
  "digital_wears",
  "electronics",
  "fashion",
  "fitness",
  "furniture",
  "gaming",
  "kitchen",
  "lighting",
  "outdoor",
  "personal_computers",
  "phones_tabs",
  "power_solutions",
  "tools",
  "other",
];

const POWER_SOLUTIONS_SUBCATEGORY_IDS = new Set([
  "generator",
  "inverter",
  "inverter-battery",
]);

function toItemCategory(
  category: string | null,
  subcategory?: string | null,
): ItemCategory {
  if (
    category === "electronics" &&
    subcategory &&
    POWER_SOLUTIONS_SUBCATEGORY_IDS.has(subcategory)
  ) {
    return "power_solutions";
  }
  const resolved = (category || "other") as ItemCategory;
  return VALID_CATEGORIES.includes(resolved) ? resolved : "other";
}

function toItemStatus(status: string | null): ItemStatus {
  return (status || "new_submission") as ItemStatus;
}

function toCoordinates(
  value: RecentlyConfirmedItemRow["coordinates"],
): Coordinates | undefined {
  if (!value) return undefined;
  const coords = value as { lat?: unknown; lng?: unknown };
  if (typeof coords.lat === "number" && typeof coords.lng === "number") {
    return { lat: coords.lat, lng: coords.lng };
  }
  return undefined;
}

function resolveWindowDays(windowDays?: number): number {
  if (windowDays != null && windowDays > 0) {
    return windowDays;
  }
  return DEFAULT_BUYER_AVAILABILITY_CONFIRMED_WINDOW_DAYS;
}

function rowsToItems(rows: RecentlyConfirmedItemRow[]): Item[] {
  return rows
    .filter((row) => row.id && row.title && row.seller_id)
    .map((row) => ({
      id: row.id,
      title: row.title,
      description: row.description || undefined,
      defects: row.defects || undefined,
      price: row.price,
      quantity: row.quantity || 1,
      category: toItemCategory(row.category, row.subcategory),
      subcategory: row.subcategory || "",
      images: [],
      location: row.location || "Lagos",
      coordinates: toCoordinates(row.coordinates),
      sellerId: row.seller_id,
      status: toItemStatus(row.status),
      sellerReviewFeedback: parseSellerReviewFeedback(
        row.seller_review_feedback,
      ),
      createdAt: row.created_at ? new Date(row.created_at) : new Date(),
      updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
      sellerAvailabilityConfirmedAt: row.seller_availability_confirmed_at
        ? new Date(row.seller_availability_confirmed_at)
        : null,
    }));
}

/**
 * Server-side fetch for the "Still available near you" shelf pool.
 * Uses service role because shelf may include listings buyers already saw elsewhere.
 */
export async function fetchRecentlyConfirmedItems(
  service: ServiceSupabase,
  options: { limit?: number; windowDays?: number } = {},
): Promise<Item[]> {
  const safeLimit = Math.min(Math.max(options.limit ?? 48, 1), 60);
  const windowDays = resolveWindowDays(options.windowDays);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - windowDays);

  const { data, error } = await service
    .from("items")
    .select(RECENTLY_CONFIRMED_ITEM_COLUMNS)
    .eq("status", "approved")
    .not("seller_availability_confirmed_at", "is", null)
    .gte("seller_availability_confirmed_at", cutoff.toISOString())
    .order("seller_availability_confirmed_at", { ascending: false })
    .limit(safeLimit)
    .returns<RecentlyConfirmedItemRow[]>();

  if (error) {
    throw error;
  }

  return rowsToItems(data ?? []);
}

/** JSON-safe shape for `/api/items/recently-confirmed`. */
export type RecentlyConfirmedItemDto = Omit<
  Item,
  "createdAt" | "updatedAt" | "sellerAvailabilityConfirmedAt" | "imageDetails"
> & {
  createdAt: string;
  updatedAt: string;
  sellerAvailabilityConfirmedAt: string | null;
};

export function serializeRecentlyConfirmedItems(
  items: Item[],
): RecentlyConfirmedItemDto[] {
  return items.map(
    ({ createdAt, updatedAt, sellerAvailabilityConfirmedAt, ...rest }) => ({
      ...rest,
      createdAt: createdAt.toISOString(),
      updatedAt: updatedAt.toISOString(),
      sellerAvailabilityConfirmedAt: sellerAvailabilityConfirmedAt
        ? sellerAvailabilityConfirmedAt.toISOString()
        : null,
    }),
  );
}

export function deserializeRecentlyConfirmedItems(
  items: RecentlyConfirmedItemDto[],
): Item[] {
  return items.map(
    ({ createdAt, updatedAt, sellerAvailabilityConfirmedAt, ...rest }) => ({
      ...rest,
      createdAt: new Date(createdAt),
      updatedAt: new Date(updatedAt),
      sellerAvailabilityConfirmedAt: sellerAvailabilityConfirmedAt
        ? new Date(sellerAvailabilityConfirmedAt)
        : null,
    }),
  );
}
